package app.smartcoding.realrate.presentation.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.TextAutoSize
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Clear
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material.icons.outlined.Lock
import androidx.compose.material.icons.outlined.LockOpen
import androidx.compose.material3.FilledTonalIconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusDirection
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import app.smartcoding.realrate.localization.LocalStrings
import app.smartcoding.realrate.presentation.util.NumberFormattingVisualTransformation
import org.jetbrains.compose.resources.painterResource
import realrate.shared.generated.resources.Res
import realrate.shared.generated.resources.add
import realrate.shared.generated.resources.remove

@Composable
fun InputField(
    textFieldValue: TextFieldValue,
    onValueChange: (TextFieldValue) -> Unit,
    label: String,
    onIncrement: (TextFieldValue) -> Unit,
    onDecrement: (TextFieldValue) -> Unit,
    modifier: Modifier = Modifier,
    isExpanded: Boolean? = null,
    isLockedTaxRate: Boolean? = null,
    isError: Boolean,
    onToggleExpand: () -> Unit = { },
    onToggleLockedTaxRate: () -> Unit = { },
    supportingText: @Composable (() -> Unit)? = null,
    prefix: @Composable (() -> Unit)? = null,
) {
    val focusRequester = remember { FocusRequester() }

    Row(
        modifier = modifier,
        verticalAlignment = Alignment.Top,
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        OutlinedTextField(
            value = textFieldValue,
            onValueChange = onValueChange,
            textStyle = LocalTextStyle.current.copy(textAlign = TextAlign.Right),
            modifier = modifier.weight(1f).focusRequester(focusRequester),
            label = {
                Text(
                    text = label,
                    maxLines = 1,
                    autoSize = TextAutoSize.StepBased(
                        minFontSize = MaterialTheme.typography.labelSmall.fontSize,
                        maxFontSize = MaterialTheme.typography.labelMedium.fontSize,
                    ),
                    softWrap = false,
                )
            },
            visualTransformation = NumberFormattingVisualTransformation(),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            keyboardActions = KeyboardActions.Default,
            singleLine = true,
            trailingIcon = if (textFieldValue.text.isNotEmpty()) { ->
                IconButton(
                    onClick = {
                        onValueChange(TextFieldValue(""))
                        focusRequester.requestFocus(
                            focusDirection = FocusDirection.Enter
                        )
                    }
                ) {
                    Icon(
                        imageVector = Icons.Filled.Clear,
                        contentDescription = "Clear text"
                    )
                }
            } else null,
            isError = isError,
            supportingText = supportingText,
            prefix = prefix,
        )
        Row(
            modifier = Modifier.padding(top = 12.dp),
            verticalAlignment = Alignment.Top,
            horizontalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            when {
                isExpanded != null -> {
                    FilledTonalIconButton(
                        onClick = onToggleExpand,
                    ) {
                        Icon(
                            imageVector = if (isExpanded) Icons.Default.KeyboardArrowUp else Icons.Default.KeyboardArrowDown,
                            contentDescription = if (isExpanded) LocalStrings.current.hideDetails else LocalStrings.current.showDetails
                        )
                    }
                }

                isLockedTaxRate != null -> {
                    FilledTonalIconButton(
                        onClick = onToggleLockedTaxRate,
                    ) {
                        Icon(
                            imageVector = if (isLockedTaxRate) Icons.Outlined.Lock else Icons.Outlined.LockOpen,
                            contentDescription = if (isLockedTaxRate) LocalStrings.current.lockTaxRate else LocalStrings.current.unlockTaxRate
                        )
                    }
                }

                else -> Spacer(modifier = Modifier.width(48.dp))
            }
            FilledTonalIconButton(
                onClick = { onDecrement(textFieldValue) },
            ) {
                Icon(
                    painterResource(Res.drawable.remove),
                    contentDescription = "Decrement",
                )
            }
            FilledTonalIconButton(
                onClick = { onIncrement(textFieldValue) },
            ) {
                Icon(
                    painterResource(Res.drawable.add),
                    contentDescription = "Increment",
                )
            }
        }
    }
}
